import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Loader2, RefreshCw, DollarSign, Users, Award, TrendingUp, Flame, CheckCircle2, Repeat, Calendar, Tag, Trophy, Download, ChevronDown, ChevronUp } from 'lucide-react';
import { BarChart, Bar, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, Cell } from 'recharts';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const PURPLE = '#6B3FA0';
const PINK = '#B71C5C';
const ENGAGEMENT_COLORS = { hot: '#DC2626', warm: '#F59E0B', cool: '#3B82F6', cold: '#6B7280', unscored: '#D1D5DB' };

const exportCsv = (filename, headers, rows) => {
  const esc = (c) => { const s = String(c ?? ''); return /[,"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const csv = [headers, ...rows].map(r => r.map(esc).join(',')).join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = filename;
  a.click();
};

function ExpandableList({ rows, initial = 10, renderRow, testid }) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? rows : rows.slice(0, initial);
  return (
    <>
      {visible.map(renderRow)}
      {rows.length > initial && (
        <button onClick={() => setShowAll(s => !s)}
                className="mt-2 text-xs text-[#6B3FA0] hover:underline flex items-center gap-1"
                data-testid={`${testid}-toggle`}>
          {showAll ? <><ChevronUp className="h-3 w-3" /> Show top {initial}</>
                   : <><ChevronDown className="h-3 w-3" /> Show all {rows.length}</>}
        </button>
      )}
    </>
  );
}

const StatTile = ({ icon: Icon, label, value, sub, testid }) => (
  <Card data-testid={testid}>
    <CardContent className="p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-gray-500">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="text-xl font-bold tabular-nums mt-0.5">{value}</div>
      {sub && <div className="text-[11px] text-gray-500">{sub}</div>}
    </CardContent>
  </Card>
);

const Section = ({ title, icon: Icon, children, testid, wide, onExport }) => (
  <Card data-testid={testid} className={wide ? 'md:col-span-2' : ''}>
    <CardContent className="p-4">
      <div className="font-semibold mb-3 flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-[#6B3FA0]" />} {title}
        {onExport && (
          <Button size="sm" variant="ghost" className="ml-auto h-7 px-2 text-xs text-gray-500 hover:text-[#6B3FA0]"
                  onClick={onExport} title="Export this report as CSV" data-testid={`${testid}-export`}>
            <Download className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      {children}
    </CardContent>
  </Card>
);

export function ReportsTab({ api }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/donors/reports/insights');
      setData(data);
    } catch { toast.error('Failed to load reports'); }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#6B3FA0]" /></div>;
  if (!data) return (
    <div className="text-center py-16 text-sm text-gray-500" data-testid="reports-error">
      Couldn't load reports. <Button size="sm" variant="outline" className="ml-2" onClick={load}>Retry</Button>
    </div>
  );

  const { headline: h, giving_by_year, monthly_trend, gift_size_distribution, seasonality_month,
    seasonality_dow, top_donors, second_gift, upgrades, streaks, acknowledgment,
    engagement_distribution, pledges, tag_performance, largest_gifts } = data;

  return (
    <div className="space-y-4" data-testid="donor-reports-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Every stat below is computed live from your gift records.</p>
        <Button size="sm" variant="ghost" onClick={load} data-testid="reports-refresh"><RefreshCw className="h-3.5 w-3.5" /></Button>
      </div>

      {/* Headline tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <StatTile icon={DollarSign} label="Lifetime raised" value={fmt$(h.lifetime_raised)} sub={`${h.gift_count} gifts`} testid="stat-lifetime" />
        <StatTile icon={DollarSign} label={`Raised ${new Date().getFullYear()}`} value={fmt$(h.ytd_raised)} sub={`${h.ytd_gifts} gifts · ${h.ytd_donors} donors`} testid="stat-ytd" />
        <StatTile icon={Users} label="Giving donors" value={h.giving_donor_count} sub={`of ${h.donor_count} records`} testid="stat-donors" />
        <StatTile icon={Award} label="Avg donor value" value={fmt$(h.avg_donor_ltv)} sub={`${h.avg_gifts_per_donor} gifts/donor`} testid="stat-ltv" />
        <StatTile icon={TrendingUp} label="Average gift" value={fmt$(h.avg_gift)} sub={`median ${fmt$(h.median_gift)}`} testid="stat-avg" />
        <StatTile icon={Trophy} label="Largest gift" value={h.largest_gift ? fmt$(h.largest_gift.amount) : '—'} sub={h.largest_gift ? `${h.largest_gift.donor_name} · ${h.largest_gift.date}` : ''} testid="stat-largest" />
        <StatTile icon={Repeat} label="2nd-gift conversion" value={`${second_gift.rate_pct}%`} sub={second_gift.median_days != null ? `median ${second_gift.median_days} days to 2nd gift` : `${second_gift.converted} of ${second_gift.eligible}`} testid="stat-second-gift" />
        <StatTile icon={CheckCircle2} label="Ack rate" value={`${acknowledgment.ack_rate_pct}%`} sub={acknowledgment.unacked_count ? `${acknowledgment.unacked_count} pending (${fmt$(acknowledgment.unacked_amount)})` : 'all thanked 🎉'} testid="stat-ack" />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Giving by year: new vs returning */}
        <Section title="Giving by year — new vs returning revenue" icon={TrendingUp} testid="report-by-year" wide>
          {giving_by_year.length === 0 ? <Empty /> : (
            <>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={giving_by_year} margin={{ top: 5, right: 6, left: 6, bottom: 0 }}>
                  <XAxis dataKey="year" fontSize={11} />
                  <YAxis fontSize={11} tickFormatter={fmt$} />
                  <Tooltip formatter={(v) => fmt$(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="new_revenue" name="New donors" stackId="a" fill={PINK} radius={[0, 0, 0, 0]} />
                  <Bar dataKey="returning_revenue" name="Returning donors" stackId="a" fill={PURPLE} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="overflow-x-auto mt-2">
                <table className="w-full text-xs">
                  <thead><tr className="text-gray-500 text-left">
                    <th className="py-1 pr-2">Year</th><th className="pr-2">Total</th><th className="pr-2">Gifts</th>
                    <th className="pr-2">Donors</th><th className="pr-2">New donors</th><th className="pr-2">Avg gift</th><th>YoY</th>
                  </tr></thead>
                  <tbody>
                    {giving_by_year.map(r => (
                      <tr key={r.year} className="border-t">
                        <td className="py-1 pr-2 font-medium">{r.year}</td>
                        <td className="pr-2 tabular-nums">{fmt$(r.total)}</td>
                        <td className="pr-2">{r.gifts}</td><td className="pr-2">{r.donors}</td>
                        <td className="pr-2">{r.new_donors}</td>
                        <td className="pr-2 tabular-nums">{fmt$(r.avg_gift)}</td>
                        <td className={r.yoy_pct == null ? 'text-gray-400' : r.yoy_pct >= 0 ? 'text-emerald-600 font-medium' : 'text-red-600 font-medium'}>
                          {r.yoy_pct == null ? '—' : `${r.yoy_pct > 0 ? '+' : ''}${r.yoy_pct}%`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Section>

        {/* Monthly trend */}
        <Section title="Monthly giving trend (last 24 months)" icon={Calendar} testid="report-monthly"
                 onExport={() => exportCsv('monthly_giving_trend.csv', ['month', 'total', 'gifts'],
                   monthly_trend.map(r => [r.month, r.total, r.gifts ?? '']))}>
          {monthly_trend.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={monthly_trend} margin={{ top: 5, right: 6, left: 6, bottom: 0 }}>
                <XAxis dataKey="month" fontSize={10} />
                <YAxis fontSize={11} tickFormatter={fmt$} />
                <Tooltip formatter={(v, n) => n === 'total' ? fmt$(v) : v} />
                <Area type="monotone" dataKey="total" stroke={PURPLE} fill="#EDE7F6" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Section>

        {/* Gift size distribution */}
        <Section title="Gift size distribution" icon={DollarSign} testid="report-size-dist">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={gift_size_distribution} margin={{ top: 5, right: 6, left: 6, bottom: 0 }}>
              <XAxis dataKey="band" fontSize={10} />
              <YAxis fontSize={11} allowDecimals={false} />
              <Tooltip formatter={(v, n, p) => n === 'count' ? [`${v} gifts · ${fmt$(p.payload.total)}`, p.payload.band] : v} />
              <Bar dataKey="count" fill={PURPLE} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Section>

        {/* Top donors leaderboard */}
        <Section title="Top donors — lifetime" icon={Trophy} testid="report-top-donors"
                 onExport={() => exportCsv('top_donors.csv', ['rank', 'name', 'total', 'gifts', 'first_gift', 'last_gift'],
                   top_donors.map((td, i) => [i + 1, td.name, td.total, td.gifts, td.first, td.last]))}>
          {top_donors.length === 0 ? <Empty /> : (
            <ol className="space-y-1.5">
              <ExpandableList rows={top_donors} testid="top-donors" renderRow={(td, i) => (
                <li key={td.donor_id} className="flex items-center gap-2 text-sm">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${i < 3 ? 'bg-[#6B3FA0] text-white' : 'bg-gray-100 text-gray-600'}`}>{i + 1}</span>
                  <button className="flex-1 min-w-0 text-left truncate hover:text-[#6B3FA0] hover:underline"
                          onClick={() => navigate(`/donors/${td.donor_id}`)} data-testid={`top-donor-${i}`}>
                    {td.name || '(unnamed)'}
                  </button>
                  <span className="text-xs text-gray-500">{td.gifts} gifts</span>
                  <span className="font-semibold tabular-nums">{fmt$(td.total)}</span>
                </li>
              )} />
            </ol>
          )}
        </Section>

        {/* Seasonality */}
        <Section title="Seasonality — giving by month" icon={Calendar} testid="report-season-month">
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={seasonality_month} margin={{ top: 5, right: 6, left: 6, bottom: 0 }}>
              <XAxis dataKey="m" fontSize={10} />
              <YAxis fontSize={11} tickFormatter={fmt$} />
              <Tooltip formatter={(v) => fmt$(v)} />
              <Bar dataKey="total" fill={PURPLE} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <p className="text-[11px] text-gray-500 mt-1">All years combined — plan appeals around your strongest months.</p>
        </Section>

        <Section title="Giving by day of week" icon={Calendar} testid="report-season-dow">
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={seasonality_dow} margin={{ top: 5, right: 6, left: 6, bottom: 0 }}>
              <XAxis dataKey="d" fontSize={10} />
              <YAxis fontSize={11} tickFormatter={fmt$} />
              <Tooltip formatter={(v) => fmt$(v)} />
              <Bar dataKey="total" fill={PINK} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <p className="text-[11px] text-gray-500 mt-1">Best days to time email sends and social asks.</p>
        </Section>

        {/* Upgrades / downgrades */}
        <Section title={`Upgrades vs ${upgrades.prev_year}`} icon={TrendingUp} testid="report-upgrades">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="border rounded-lg p-3 bg-emerald-50 border-emerald-200">
              <div className="text-2xl font-bold text-emerald-700">{upgrades.upgraded}</div>
              <div className="text-[11px] text-emerald-700">gave MORE</div>
            </div>
            <div className="border rounded-lg p-3">
              <div className="text-2xl font-bold text-gray-700">{upgrades.maintained}</div>
              <div className="text-[11px] text-gray-500">same amount</div>
            </div>
            <div className="border rounded-lg p-3 bg-red-50 border-red-200">
              <div className="text-2xl font-bold text-red-700">{upgrades.downgraded}</div>
              <div className="text-[11px] text-red-700">gave LESS</div>
            </div>
          </div>
          <p className="text-[11px] text-gray-500 mt-2">Donors who gave in both {upgrades.prev_year} and {upgrades.year} — downgraders are prime stewardship targets.</p>
        </Section>

        {/* Streaks */}
        <Section title="Loyalty streaks — consecutive years of giving" icon={Flame} testid="report-streaks"
                 onExport={() => exportCsv('loyalty_streaks.csv', ['name', 'streak_years', 'lifetime_total'],
                   streaks.map(s => [s.name, s.streak, s.total]))}>
          {streaks.length === 0 ? <p className="text-sm text-gray-500">No active multi-year streaks yet.</p> : (
            <ol className="space-y-1.5">
              <ExpandableList rows={streaks} testid="streaks" renderRow={(s) => (
                <li key={s.donor_id} className="flex items-center gap-2 text-sm">
                  <Flame className={`h-4 w-4 shrink-0 ${s.streak >= 4 ? 'text-red-500' : 'text-orange-400'}`} />
                  <button className="flex-1 min-w-0 text-left truncate hover:text-[#6B3FA0] hover:underline"
                          onClick={() => navigate(`/donors/${s.donor_id}`)}>{s.name || '(unnamed)'}</button>
                  <Badge className="bg-orange-100 text-orange-800 border-0 text-[10px]">{s.streak} yrs</Badge>
                  <span className="text-xs text-gray-500 tabular-nums">{fmt$(s.total)}</span>
                </li>
              )} />
            </ol>
          )}
        </Section>

        {/* Engagement distribution */}
        <Section title="Engagement score distribution" icon={Users} testid="report-engagement">
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={engagement_distribution} margin={{ top: 5, right: 6, left: 6, bottom: 0 }}>
              <XAxis dataKey="bucket" fontSize={11} className="capitalize" />
              <YAxis fontSize={11} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                {engagement_distribution.map(e => <Cell key={e.bucket} fill={ENGAGEMENT_COLORS[e.bucket] || PURPLE} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Section>

        {/* Pledge fulfillment */}
        <Section title="Pledge fulfillment" icon={CheckCircle2} testid="report-pledges">
          {pledges.count === 0 ? <p className="text-sm text-gray-500">No pledges recorded yet.</p> : (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>{fmt$(pledges.paid)} collected of {fmt$(pledges.pledged)} pledged</span>
                <span className="font-bold">{pledges.fulfillment_pct}%</span>
              </div>
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-[#6B3FA0] rounded-full" style={{ width: `${Math.min(pledges.fulfillment_pct, 100)}%` }} />
              </div>
              <p className="text-[11px] text-gray-500">{pledges.count} pledge{pledges.count === 1 ? '' : 's'} on file.</p>
            </div>
          )}
        </Section>

        {/* Tag performance */}
        <Section title="Giving by tag" icon={Tag} testid="report-tags"
                 onExport={() => exportCsv('giving_by_tag.csv', ['tag', 'donors', 'total'],
                   tag_performance.map(t => [t.tag, t.donors, t.total]))}>
          {tag_performance.length === 0 ? <p className="text-sm text-gray-500">Tag donors to see segment performance here.</p> : (
            <div className="space-y-1.5">
              <ExpandableList rows={tag_performance} testid="tags" renderRow={(t) => {
                const max = tag_performance[0].total || 1;
                return (
                  <div key={t.tag} className="flex items-center gap-2 text-sm">
                    <span className="w-32 truncate text-gray-700">{t.tag}</span>
                    <div className="flex-1 h-4 bg-gray-100 rounded overflow-hidden">
                      <div className="h-full bg-[#8B5CF6]" style={{ width: `${Math.round(t.total / max * 100)}%` }} />
                    </div>
                    <span className="text-xs text-gray-500 w-14">{t.donors} donor{t.donors === 1 ? '' : 's'}</span>
                    <span className="font-medium tabular-nums w-20 text-right">{fmt$(t.total)}</span>
                  </div>
                );
              }} />
            </div>
          )}
        </Section>

        {/* Largest gifts */}
        <Section title="Largest gifts of all time" icon={Trophy} testid="report-largest"
                 onExport={() => exportCsv('largest_gifts.csv', ['rank', 'donor_name', 'amount', 'date', 'campaign'],
                   largest_gifts.map((g, i) => [i + 1, g.donor_name, g.amount, g.date, g.campaign]))}>
          {largest_gifts.length === 0 ? <Empty /> : (
            <div className="space-y-1">
              <ExpandableList rows={largest_gifts} testid="largest" renderRow={(g, i) => (
                <div key={i} className="flex items-center gap-2 text-sm border-t first:border-t-0 py-1">
                  <span className="text-xs text-gray-400 w-4">{i + 1}</span>
                  <span className="flex-1 truncate">{g.donor_name || '(unnamed)'}</span>
                  {g.campaign && <Badge variant="outline" className="text-[10px]">{g.campaign}</Badge>}
                  <span className="text-xs text-gray-500">{g.date}</span>
                  <span className="font-bold tabular-nums">{fmt$(g.amount)}</span>
                </div>
              )} />
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}

function Empty() {
  return <p className="text-sm text-gray-500">Not enough data yet — log a few gifts first.</p>;
}
